<script setup lang="ts">
import { onMounted, ref, onUnmounted } from 'vue';
import * as THREE from 'three';

const container = ref<HTMLElement | null>(null);
const isSupported = ref(true);

let scene: THREE.Scene;
let camera: THREE.PerspectiveCamera;
let renderer: THREE.WebGLRenderer;
let time = 0;
let scrollY = 0;
let mouse = new THREE.Vector2(-9999, -9999);
const hasMouseMoved = ref(false);
const opacity = ref(0);

const ampMultiplier = ref(1.0);

let visibleWidth = 80;
let visibleHeight = 40;

function updateViewportBounds() {
  if (!camera) return;
  const aspect = window.innerWidth / window.innerHeight;
  const vFov = (camera.fov * Math.PI) / 180;
  visibleHeight = 2 * Math.tan(vFov / 2) * camera.position.z;
  visibleWidth = visibleHeight * aspect;
}

function updateResponsiveSizes() {
  const isMobile = window.innerWidth < 768;
  ampMultiplier.value = isMobile ? 0.6 : 1.0;
  if (camera) {
    camera.position.z = isMobile ? 65 : 40;
  }
  updateViewportBounds();
}

// Check WebGL Support
function checkWebGLSupport(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl', { failIfMajorPerformanceCaveat: true }) || 
               canvas.getContext('experimental-webgl', { failIfMajorPerformanceCaveat: true })) as WebGLRenderingContext | null;
    
    if (!gl) return false;
    
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      const rendererName = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      if (rendererName && (
        rendererName.toLowerCase().includes('swiftshader') || 
        rendererName.toLowerCase().includes('software') ||
        rendererName.toLowerCase().includes('microsoft basic')
      )) {
        return false;
      }
    }
    
    return true;
  } catch (e) {
    return false;
  }
}

const WAVE_COUNT = 3;
const POINTS_PER_WAVE = 300;

interface WaveInstance {
  points: THREE.Points;
  geometry: THREE.BufferGeometry;
  originalPositions: Float32Array;
  yBase: number;
  zBase: number;
  speed: number;
  amplitude: number;
  frequency: number;
}

let waves: WaveInstance[] = [];

// Particle & Ripple Definitions
interface LetterParticle {
  sprite: THREE.Sprite;
  velocity: THREE.Vector3;
  rotationSpeed: number;
  life: number;
  decay: number;
  scaleX: number;
  scaleY: number;
}
let letterParticles: LetterParticle[] = [];

interface DustParticle {
  sprite: THREE.Sprite;
  velocity: THREE.Vector3;
  life: number;
  decay: number;
}
let dustParticles: DustParticle[] = [];

interface TapRipple {
  mesh: THREE.Mesh;
  material: THREE.MeshBasicMaterial;
  radius: number;
  maxRadius: number;
  speed: number;
  opacity: number;
  x: number;
  y: number;
  z: number;
}
let tapRipples: TapRipple[] = [];

// Text textures storage
const charTextures: Record<string, THREE.CanvasTexture> = {};
const allowedChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789$+-?!. %*()[]{}";
let dotTexture: THREE.CanvasTexture;

// Texture Generation
function createDotTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const grad = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    grad.addColorStop(0, 'rgba(255, 255, 255, 1)');
    grad.addColorStop(0.3, 'rgba(202, 255, 1, 0.85)');
    grad.addColorStop(1, 'rgba(202, 255, 1, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 32, 32);
  }
  dotTexture = new THREE.CanvasTexture(canvas);
}

function createCharTextures() {
  allowedChars.split('').forEach(char => {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'rgba(0,0,0,0)';
      ctx.fillRect(0, 0, 64, 64);
      ctx.font = 'bold 44px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Futuristic neon green glowing letters
      ctx.shadowColor = 'rgba(202, 255, 1, 0.95)';
      ctx.shadowBlur = 14;
      ctx.fillStyle = '#ffffff';
      ctx.fillText(char, 32, 32);
    }
    const texture = new THREE.CanvasTexture(canvas);
    charTextures[char] = texture;
  });
}

let sequenceTimeouts: ReturnType<typeof setTimeout>[] = [];

// Spawns "Speak. Type. Bet." sequentially letter by letter at the click coordinates
function spawnSpeakTypeBetSequence(clickX: number, clickY: number, z: number = 0) {
  const words = ["Speak", "Type", "Bet"];
  const letterSpacing = 0.95;
  const lineSpacing = 1.7;
  const typingSpeed = 70; // ms per letter
  
  let totalDelay = 0;
  
  words.forEach((word, wordIdx) => {
    const yPos = clickY - wordIdx * lineSpacing;
    const startX = clickX - ((word.length - 1) * letterSpacing) / 2;
    
    // Brief pause between words to differentiate them
    if (wordIdx > 0) {
      totalDelay += 180;
    }
    
    word.split('').forEach((char, charIdx) => {
      const xPos = startX + charIdx * letterSpacing;
      const t = setTimeout(() => {
        if (scene && isSupported.value) {
          spawnLetter(char, xPos, yPos, z);
        }
        sequenceTimeouts = sequenceTimeouts.filter(x => x !== t);
      }, totalDelay);
      sequenceTimeouts.push(t);
      totalDelay += typingSpeed;
    });
  });
}

// Particle Spawners
function spawnLetter(char: string, x: number, y: number, z: number = 0) {
  const texture = charTextures[char] || charTextures[' '];
  if (!texture) return;

  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    opacity: 1.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false
  });

  const sprite = new THREE.Sprite(material);
  sprite.scale.set(1.5, 1.5, 1);
  sprite.position.set(x, y, z);
  scene.add(sprite);

  letterParticles.push({
    sprite,
    velocity: new THREE.Vector3(
      (Math.random() - 0.5) * 0.015,
      0.035 + Math.random() * 0.02,
      0
    ),
    rotationSpeed: (Math.random() - 0.5) * 0.015,
    life: 1.0,
    decay: 0.006 + Math.random() * 0.004,
    scaleX: 1.5,
    scaleY: 1.5
  });
}

// Further reduced the count and sizes of the particle burst
function spawnParticleBurst(x: number, y: number, z: number = 0) {
  const count = 4; // Reduced from 5
  for (let i = 0; i < count; i++) {
    const material = new THREE.SpriteMaterial({
      map: dotTexture,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    const sprite = new THREE.Sprite(material);
    const scale = 0.08 + Math.random() * 0.18; // Reduced from 0.15 + 0.35
    sprite.scale.set(scale, scale, 1);
    sprite.position.set(x, y, z);
    scene.add(sprite);

    const angle = Math.random() * Math.PI * 2;
    const speed = 0.02 + Math.random() * 0.05; // Slower velocity

    dustParticles.push({
      sprite,
      velocity: new THREE.Vector3(
        Math.cos(angle) * speed,
        Math.sin(angle) * speed,
        (Math.random() - 0.5) * 0.01
      ),
      life: 1.0,
      decay: 0.015 + Math.random() * 0.025
    });
  }
}

// Further reduced the ripple inner/outer geometries and maximum radii
function spawnTapRipple(x: number, y: number, z: number = 0) {
  const geometry = new THREE.RingGeometry(0.9, 1.0, 32); // Uniform ring geometry
  const material = new THREE.MeshBasicMaterial({
    color: 0xcaff01,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.85,
    blending: THREE.AdditiveBlending,
    depthWrite: false
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(x, y, z);
  scene.add(mesh);

  tapRipples.push({
    mesh,
    material,
    radius: 0.1, // Reduced starting radius
    maxRadius: 3.5 + Math.random() * 1.0, // Reduced max radius from 8-10 to 3.5-4.5
    speed: 0.12, // Reduced expansion speed
    opacity: 0.85,
    x, y, z
  });

  spawnParticleBurst(x, y, z);
}

let randomTapTimeout: ReturnType<typeof setTimeout> | null = null;
function scheduleRandomTap() {
  // Delay between 800ms and 2800ms (0.8 to 2.8 seconds) for increased tap frequency
  const delay = 800 + Math.random() * 2000;
  randomTapTimeout = setTimeout(() => {
    if (scene && isSupported.value) {
      // Spawn at random coordinates across the full screen, with a 5% margin padding
      const rx = (Math.random() - 0.5) * visibleWidth * 0.9;
      const ry = (Math.random() - 0.5) * visibleHeight * 0.9;
      spawnTapRipple(rx, ry, 0);
    }
    scheduleRandomTap();
  }, delay);
}

// Raycaster & Mouse Click Setup
const mouseWorldPos = new THREE.Vector3();
const raycaster = new THREE.Raycaster();
const depthPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);

// Helper to get mouse pointer coordinates projected onto Z=0 plane
function getMouseWorldPositionZ0(): THREE.Vector3 {
  const result = new THREE.Vector3();
  if (camera) {
    raycaster.setFromCamera(mouse, camera);
    const z0Plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    raycaster.ray.intersectPlane(z0Plane, result);
  }
  return result;
}

function handleWindowClick(event: MouseEvent) {
  if (!camera || !renderer || !scene) return;
  
  const clickMouse = new THREE.Vector2();
  clickMouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  clickMouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  
  raycaster.setFromCamera(clickMouse, camera);
  const clickWorldPos = new THREE.Vector3();
  const z0Plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  raycaster.ray.intersectPlane(z0Plane, clickWorldPos);
  
  spawnTapRipple(clickWorldPos.x, clickWorldPos.y, 0);
  spawnSpeakTypeBetSequence(clickWorldPos.x, clickWorldPos.y, 0);
}

onMounted(() => {
  isSupported.value = checkWebGLSupport();
  if (!isSupported.value) {
    console.warn('3D Waveform disabled: WebGL hardware acceleration is not supported or is disabled.');
    return;
  }

  if (!container.value) return;

  try {
    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    updateResponsiveSizes();

    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.value.appendChild(renderer.domElement);

    const neonGreen = new THREE.Color(0xcaff01);
    const darkGreen = new THREE.Color(0x507800);

    // Initialise Textures
    createDotTexture();
    createCharTextures();

    // Sound wave design
    const waveConfigs = [
      { amp: 8, freq: 0.1, speed: 2.0, color: neonGreen, baseOpacity: 0.8 },
      { amp: 12, freq: 0.05, speed: 1.2, color: neonGreen, baseOpacity: 0.4 },
      { amp: 5, freq: 0.2, speed: 3.5, color: darkGreen, baseOpacity: 0.6 }
    ];

    waveConfigs.forEach((config, wIdx) => {
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(POINTS_PER_WAVE * 2 * 3);
      const originalPositions = new Float32Array(POINTS_PER_WAVE * 2 * 3);
      const colors = new Float32Array(POINTS_PER_WAVE * 2 * 3);
      const sizes = new Float32Array(POINTS_PER_WAVE * 2);

      const zBase = (wIdx - 1) * 5;

      for (let i = 0; i < POINTS_PER_WAVE; i++) {
        const x = (i / (POINTS_PER_WAVE - 1)) * 120 - 60;
        const idxTop = i * 3;
        positions[idxTop] = x;
        positions[idxTop + 1] = 0;
        positions[idxTop + 2] = zBase;
        originalPositions[idxTop] = x;
        originalPositions[idxTop + 1] = 0;
        originalPositions[idxTop + 2] = zBase;

        const idxBot = (POINTS_PER_WAVE + i) * 3;
        positions[idxBot] = x;
        positions[idxBot + 1] = 0;
        positions[idxBot + 2] = zBase;
        originalPositions[idxBot] = x;
        originalPositions[idxBot + 1] = 0;
        originalPositions[idxBot + 2] = zBase;

        colors[idxTop] = config.color.r;
        colors[idxTop + 1] = config.color.g;
        colors[idxTop + 2] = config.color.b;
        colors[idxBot] = config.color.r;
        colors[idxBot + 1] = config.color.g;
        colors[idxBot + 2] = config.color.b;

        sizes[i] = 1.0;
        sizes[POINTS_PER_WAVE + i] = 1.0;
      }

      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

      const material = new THREE.ShaderMaterial({
        transparent: true,
        blending: THREE.AdditiveBlending,
        uniforms: {
          uTime: { value: 0 },
          uOpacity: { value: config.baseOpacity }
        },
        vertexShader: `
          attribute float size;
          varying vec3 vColor;
          void main() {
            vColor = color;
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            gl_PointSize = size * (350.0 / -mvPosition.z);
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          varying vec3 vColor;
          uniform float uOpacity;
          void main() {
            float d = length(gl_PointCoord - vec2(0.5));
            if (d > 0.5) discard;
            float strength = 1.0 - d * 2.0;
            gl_FragColor = vec4(vColor, strength * uOpacity);
          }
        `,
        vertexColors: true
      });

      const mesh = new THREE.Points(geometry, material);
      scene.add(mesh);

      waves.push({
        points: mesh,
        geometry,
        originalPositions,
        yBase: 0,
        zBase,
        speed: config.speed,
        amplitude: config.amp,
        frequency: config.freq
      });
    });

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('resize', onWindowResize);
    window.addEventListener('scroll', onScroll);
    window.addEventListener('click', handleWindowClick);

    scheduleRandomTap();
    animate();
  } catch (error) {
    console.error('Failed to initialize 3D Waveform (Three.js WebGLRenderer):', error);
    isSupported.value = false;
  }
});

function onMouseMove(event: MouseEvent) {
  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  hasMouseMoved.value = true;
}

function onWindowResize() {
  if (!camera || !renderer) return;
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  updateResponsiveSizes();
}

function onScroll() {
  scrollY = window.scrollY;
}

let animationId: number;
function animate() {
  animationId = requestAnimationFrame(animate);
  time += 0.02;

  // Smooth fade in
  if (opacity.value < 1) opacity.value += 0.01;

  const scrollFactor = scrollY * 0.005;
  if (camera && hasMouseMoved.value) {
    raycaster.setFromCamera(mouse, camera);
  }

  // Update Waves
  waves.forEach((wave) => {
    const positions = wave.geometry.attributes.position.array as Float32Array;
    const sizes = wave.geometry.attributes.size.array as Float32Array;

    if (hasMouseMoved.value) {
      depthPlane.constant = -wave.zBase;
      raycaster.ray.intersectPlane(depthPlane, mouseWorldPos);
    }

    for (let i = 0; i < POINTS_PER_WAVE; i++) {
      const x = wave.originalPositions[i * 3];
      const distFromCenter = Math.abs(x) / 60;
      const taper = Math.pow(Math.max(0, 1.0 - distFromCenter), 2.0);
      
      const waveInput = x * wave.frequency + time * wave.speed + scrollFactor;
      let amp = wave.amplitude * taper * ampMultiplier.value;
      
      amp *= (0.5 + 0.5 * Math.sin(waveInput * 3.0 + time * 2.0));
      amp *= (0.8 + 0.2 * Math.sin(time * 10.0 + i)); 
      
      const yOffset = Math.sin(waveInput) * amp;
      const idxTop = i * 3;
      const idxBot = (POINTS_PER_WAVE + i) * 3;
      
      // Calculate physics/tap interaction from active ripples
      let rippleForceSum = 0;
      tapRipples.forEach((ripple) => {
        const dx = x - ripple.x;
        const dy = yOffset - ripple.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        const diff = Math.abs(dist - ripple.radius);
        const threshold = 1.2; // Tighter threshold matching the ring radius
        if (diff < threshold) {
          const factor = 1.0 - diff / threshold;
          rippleForceSum += factor * ripple.opacity * 1.5; // Even more subtle force
        }
      });
      
      positions[idxTop + 1] = yOffset + rippleForceSum;
      positions[idxBot + 1] = -yOffset - rippleForceSum;
      
      sizes[i] = 1.0 + rippleForceSum * 0.45;
      sizes[POINTS_PER_WAVE + i] = 1.0 + rippleForceSum * 0.45;
      
      // Mouse Interaction
      if (hasMouseMoved.value) {
        const dx = x - mouseWorldPos.x;
        const dy = yOffset - mouseWorldPos.y;
        const distSq = dx * dx + dy * dy;
        const interactionRadiusSq = 100;
        
        if (distSq < interactionRadiusSq) {
          const dist = Math.max(Math.sqrt(distSq), 0.1);
          const force = (1 - dist / 10) * 8;
          positions[idxTop + 1] += force;
          positions[idxBot + 1] -= force;
          sizes[i] += force * 0.8;
          sizes[POINTS_PER_WAVE + i] += force * 0.8;
        }
      }
    }

    wave.geometry.attributes.position.needsUpdate = true;
    wave.geometry.attributes.size.needsUpdate = true;
    
    if (hasMouseMoved.value) {
      wave.points.rotation.y = THREE.MathUtils.lerp(wave.points.rotation.y, mouse.x * 0.15, 0.05);
      wave.points.rotation.x = THREE.MathUtils.lerp(wave.points.rotation.x, -mouse.y * 0.1, 0.05);
    }
  });

  // Update Letter Particles
  letterParticles.forEach((p, idx) => {
    p.sprite.position.add(p.velocity);
    p.sprite.material.rotation += p.rotationSpeed;
    p.life -= p.decay;
    
    p.sprite.material.opacity = p.life * opacity.value;
    p.sprite.scale.set(
      p.scaleX * (0.4 + 0.6 * p.life),
      p.scaleY * (0.4 + 0.6 * p.life),
      1
    );
    
    if (p.life <= 0) {
      scene.remove(p.sprite);
      p.sprite.geometry.dispose();
      p.sprite.material.dispose();
      letterParticles.splice(idx, 1);
    }
  });

  // Update Dust Particles
  dustParticles.forEach((p, idx) => {
    p.sprite.position.add(p.velocity);
    p.life -= p.decay;
    
    p.sprite.material.opacity = p.life * opacity.value;
    
    if (p.life <= 0) {
      scene.remove(p.sprite);
      p.sprite.geometry.dispose();
      p.sprite.material.dispose();
      dustParticles.splice(idx, 1);
    }
  });

  // Update Tap Ripples
  tapRipples.forEach((ripple, idx) => {
    ripple.radius += ripple.speed;
    ripple.opacity = (1.0 - ripple.radius / ripple.maxRadius) * 0.85;
    
    ripple.mesh.scale.setScalar(ripple.radius);
    ripple.material.opacity = ripple.opacity * opacity.value;
    
    if (ripple.radius >= ripple.maxRadius) {
      scene.remove(ripple.mesh);
      ripple.mesh.geometry.dispose();
      ripple.material.dispose();
      tapRipples.splice(idx, 1);
    }
  });

  if (renderer && scene && camera) {
    renderer.render(scene, camera);
  }
}

onUnmounted(() => {
  window.removeEventListener('mousemove', onMouseMove);
  window.removeEventListener('resize', onWindowResize);
  window.removeEventListener('scroll', onScroll);
  window.removeEventListener('click', handleWindowClick);
  
  if (randomTapTimeout) clearTimeout(randomTapTimeout);
  sequenceTimeouts.forEach(t => clearTimeout(t));
  sequenceTimeouts = [];
  
  if (animationId) cancelAnimationFrame(animationId);
  if (renderer) renderer.dispose();
  
  // Dispose Textures
  Object.values(charTextures).forEach(t => t.dispose());
  if (dotTexture) dotTexture.dispose();
  
  // Dispose Geometries/Materials/Meshes
  letterParticles.forEach(p => {
    scene.remove(p.sprite);
    p.sprite.geometry.dispose();
    p.sprite.material.dispose();
  });
  dustParticles.forEach(p => {
    scene.remove(p.sprite);
    p.sprite.geometry.dispose();
    p.sprite.material.dispose();
  });
  tapRipples.forEach(r => {
    scene.remove(r.mesh);
    r.mesh.geometry.dispose();
    r.material.dispose();
  });
  
  waves = [];
});
</script>

<template>
  <div v-if="isSupported" ref="container" class="waveform-container" :style="{ opacity: opacity * 0.6 }"></div>
</template>

<style scoped>
.waveform-container {
  width: 100%;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 0;
  pointer-events: none;
  transition: opacity 1s ease;
}
</style>
